/**
 * RaceLobbyWindow — M7: Race lobby UI on the window substrate.
 *
 * Replaces the legacy GmRaceLobbyHud overlay with a first-class engine
 * window (ToolWindow chrome, draggable, resizable). Shows the live
 * GM Race participant list as framed rows that can be drag-reordered
 * in the UI (client-side reordering, does not affect server state).
 *
 * Data source is the ROOM SCHEMA (state.gmEvent + per-player eventId),
 * not the gmEventAnnouncement broadcast: for auto-created events the
 * broadcast fires at room-create time before the first joiner's
 * message handlers exist, so a broadcast-gated window would never
 * render. The schema is always readable late.
 *
 * The window auto-opens on gm_race_lobby during the 'announced' phase
 * via RaceLobbyAutoOpener (HUD.tsx) and closes when the phase advances.
 *
 * @see docs/design/gm/GM_RACE_MVP_PTR_CARD.md
 */

import React, { useEffect, useState } from 'react';
import { getStateCallbacks } from '@colyseus/sdk';
import { useGmEvent } from '../../../services/GmEventService';
import { COLORS, FONTS, SPACE } from '../../theme';
import { gameLogger } from '../../../../utils/GameLogger';

function getRoom(): any {
  return (globalThis as any).nm?.getRoom?.() ?? null;
}

function getLocalSessionId(): string | null {
  return getRoom()?.sessionId ?? null;
}

function getLocalGmLevel(): number {
  const room = getRoom();
  if (!room) return 0;
  const sid = room.sessionId;
  const player = room.state?.players?.get(sid);
  return player?.gmLevel ?? 0;
}

interface Participant {
  sessionId: string;
  heroName: string;
}

/** Room-schema gmEvent snapshot (id/phase/title/participantCount). */
function getSchemaGmEvent(): { id: string; phase: string; title: string; participantCount: number } | null {
  const ge = getRoom()?.state?.gmEvent;
  if (!ge || !ge.id) return null;
  return {
    id: ge.id,
    phase: ge.phase,
    title: ge.title,
    participantCount: ge.participantCount ?? 0,
  };
}

/** Joined racers straight from the schema: players whose eventId matches. */
function getSchemaParticipants(eventId: string): Participant[] {
  const players = getRoom()?.state?.players;
  const out: Participant[] = [];
  if (!players?.forEach) return out;
  players.forEach((p: any, sid: string) => {
    if (p?.eventId === eventId) out.push({ sessionId: sid, heroName: p.heroName ?? sid });
  });
  return out;
}

/**
 * Hook that subscribes to the room schema and returns the live race lobby
 * state. Mirrors the logic in the legacy GmRaceLobbyHud but is reusable
 * from the window content component.
 */
function useRaceLobbySchema(): {
  gmEventId: string | null;
  gmEventPhase: string | null;
  gmEventTitle: string;
  participantCount: number;
  participants: Participant[];
} {
  const [gmEventId, setGmEventId] = useState<string | null>(null);
  const [gmEventPhase, setGmEventPhase] = useState<string | null>(null);
  const [gmEventTitle, setGmEventTitle] = useState<string>('');
  const [participantCount, setParticipantCount] = useState<number>(0);
  const [participants, setParticipants] = useState<Participant[]>([]);

  useEffect(() => {
    const room = getRoom();
    if (!room) return;

    const unsubs: Array<() => void> = [];

    const setupCallbacks = () => {
      try {
        const updateFromSchema = () => {
          const gmEvent = getSchemaGmEvent();
          if (!gmEvent) {
            setGmEventId(null);
            setGmEventPhase(null);
            setGmEventTitle('');
            setParticipantCount(0);
            setParticipants([]);
            return;
          }
          setGmEventId(gmEvent.id);
          setGmEventPhase(gmEvent.phase);
          setGmEventTitle(gmEvent.title);
          setParticipantCount(gmEvent.participantCount);
          setParticipants(getSchemaParticipants(gmEvent.id));
        };

        updateFromSchema();

        const state = room.state;
        if (!state) return;
        const proxy = getStateCallbacks(state) as any;

        const unsubPlayers = proxy.players.onAdd((player: any) => {
          updateFromSchema();
          player.listen?.('eventId', () => {
            updateFromSchema();
          });
        });

        const unsubPlayersRemove = proxy.players.onRemove(() => {
          updateFromSchema();
        });

        const unsubGmEvent = proxy.gmEvent?.onChange?.(() => {
          updateFromSchema();
        });

        unsubs.push(unsubPlayers);
        unsubs.push(unsubPlayersRemove);
        if (unsubGmEvent) unsubs.push(unsubGmEvent);
      } catch (err) {
        gameLogger.error('ui', 'RaceLobbyWindow failed to load Colyseus SDK callbacks:', { error: err });
      }
    };

    setupCallbacks();

    return () => {
      unsubs.forEach((unsub) => unsub());
    };
  }, []);

  return { gmEventId, gmEventPhase, gmEventTitle, participantCount, participants };
}

/**
 * Draggable framed participant rows.
 * Supports drag-and-drop reordering within the list. The order is local
 * state only; it does not affect server state or the event start order.
 */
function DraggableParticipantList({
  participants,
  localSid,
  onReorder,
}: {
  participants: Participant[];
  localSid: string | null;
  onReorder: (reordered: Participant[]) => void;
}) {
  const [draggingId, setDraggingId] = React.useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, sessionId: string) => {
    setDraggingId(sessionId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', sessionId);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetSessionId: string) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData('text/plain');
    setDraggingId(null);
    if (!sourceId || sourceId === targetSessionId) return;

    const sourceIndex = participants.findIndex((p) => p.sessionId === sourceId);
    const targetIndex = participants.findIndex((p) => p.sessionId === targetSessionId);
    if (sourceIndex === -1 || targetIndex === -1) return;

    const next = [...participants];
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);
    onReorder(next);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {participants.map((entry, i) => {
        const isMe = entry.sessionId === localSid;
        const isDragging = draggingId === entry.sessionId;
        return (
          <div
            key={entry.sessionId}
            draggable
            onDragStart={(e) => handleDragStart(e, entry.sessionId)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, entry.sessionId)}
            onDragEnd={handleDragEnd}
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              border: `1px solid ${isDragging ? COLORS.borderStrong : COLORS.border}`,
              background: isDragging ? COLORS.surfaceHover : isMe ? 'rgba(255,255,255,0.06)' : COLORS.surfaceSunken,
              fontSize: 13,
              fontFamily: FONTS.ui,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'move',
              opacity: isDragging ? 0.7 : 1,
              transition: 'background 0.1s, border-color 0.1s, opacity 0.1s',
              color: isMe ? COLORS.textAccent : COLORS.textPrimary,
              fontWeight: isMe ? 'bold' : 'normal',
              boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.35)' : 'none',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: COLORS.textMuted, minWidth: 20, textAlign: 'right' }}>
                {i + 1}.
              </span>
              <span>{entry.heroName}{isMe ? ' (You)' : ''}</span>
            </span>
            <span style={{ color: COLORS.textMuted, fontSize: 11, cursor: 'grab' }}>⋮⋮</span>
          </div>
        );
      })}
    </div>
  );
}

/** Race lobby window content — rendered inside the engine ToolWindow chrome. */
export const RaceLobbyWindowContent: React.FC = () => {
  const { announcement, actions } = useGmEvent();
  const {
    gmEventId,
    gmEventPhase,
    gmEventTitle,
    participantCount,
    participants: schemaParticipants,
  } = useRaceLobbySchema();

  // Local ordered view. Starts from the schema order and allows drag-reorder.
  const [orderedParticipants, setOrderedParticipants] = useState<Participant[]>([]);

  // Reset local order when the underlying schema list changes (new joins,
  // leaves, or phase changes) — this keeps the live list correct and is the
  // key to the "missing player" fix: the hook updates on players.onAdd, so
  // late joiners appear immediately.
  useEffect(() => {
    setOrderedParticipants(schemaParticipants);
  }, [schemaParticipants]);

  const isAdmin = getLocalGmLevel() >= 5;
  const localSid = getLocalSessionId();

  // maxParticipants only travels on the (missable) announcement broadcast;
  // enrich when present, degrade to a bare count when not.
  const maxPlayers = gmEventId && announcement?.eventId === gmEventId ? announcement.maxParticipants : null;

  if (!gmEventId || gmEventPhase !== 'announced') {
    return (
      <div style={{ padding: SPACE.md, color: COLORS.textMuted, fontFamily: FONTS.ui, fontSize: 13 }}>
        No race lobby is currently active.
      </div>
    );
  }

  return (
    <div style={{ padding: SPACE.md, fontFamily: FONTS.ui, display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
      <div style={{ textAlign: 'center' }}>
        <h2 style={{ margin: 0, fontSize: 18, color: COLORS.textAccent }}>
          🏁 {gmEventTitle || 'GM Race'}
        </h2>
        <div style={{ marginTop: 4, fontSize: 12, color: COLORS.textSecondary }}>
          {maxPlayers ? `${participantCount}/${maxPlayers}` : participantCount} player{participantCount === 1 ? '' : 's'} in lobby
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {orderedParticipants.length === 0 ? (
          <div style={{ padding: '12px 0', color: COLORS.textMuted, fontSize: 13, textAlign: 'center' }}>
            No players yet...
          </div>
        ) : (
          <DraggableParticipantList
            participants={orderedParticipants}
            localSid={localSid}
            onReorder={setOrderedParticipants}
          />
        )}
      </div>

      <div style={{ flexShrink: 0, marginTop: 'auto' }}>
        {isAdmin ? (
          <button
            type="button"
            // Empty lobby → the server's staging gate would just cancel the
            // event ("Not enough participants") — refuse client-side with a
            // reason instead of a silent dead-end (PTR find 2026-07-10).
            disabled={participantCount === 0}
            title={participantCount === 0 ? 'No participants yet — players must join before the race can start' : undefined}
            onClick={() => actions.start(gmEventId)}
            style={{
              width: '100%',
              padding: '10px 16px',
              background: participantCount === 0 ? COLORS.surfaceHover : COLORS.success,
              color: participantCount === 0 ? COLORS.textMuted : '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 15,
              fontWeight: 'bold',
              cursor: participantCount === 0 ? 'not-allowed' : 'pointer',
              fontFamily: FONTS.ui,
            }}
          >
            {participantCount === 0 ? '▶ Start Race (no players joined)' : '▶ Start Race'}
          </button>
        ) : (
          <div style={{
            padding: '10px 16px',
            background: COLORS.surfaceHover,
            color: COLORS.textMuted,
            borderRadius: 6,
            fontSize: 13,
            textAlign: 'center',
            fontFamily: FONTS.ui,
          }}>
            Waiting for DJ to start the race...
          </div>
        )}
      </div>
    </div>
  );
};
